/**
 * 时间的显示格式。
 *
 * 一律按浏览器所在时区显示——arron 人在国内，看到的就该是国内时间；
 * 后端给的是带时区的 ISO 串，怎么转是显示层的事。
 *
 * 这里不放任何句子，只放格式。带字的（「今天」「昨天」）走 `src/copy/`。
 */

const HHMM = new Intl.DateTimeFormat('zh-CN', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

/** `02:41`。时间线、探测流、排班都用它。 */
export function hhmm(iso: string): string {
  return HHMM.format(new Date(iso))
}

/** `7/29`。跨天之后的那些日期，短到能塞进 46px 的一列。 */
export function shortDate(iso: string): string {
  const d = new Date(iso)
  return `${d.getMonth() + 1}/${d.getDate()}`
}

/** 差了几个整天。0 是今天，1 是昨天。 */
export function daysBetween(iso: string, now: Date = new Date()): number {
  const then = new Date(iso)
  const a = Date.UTC(then.getFullYear(), then.getMonth(), then.getDate())
  const b = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((b - a) / 86_400_000)
}

/** 托管了多少天。机器详情那行元信息里的「已托管 143 天」。 */
export function managedDays(createdAt: string, now: Date = new Date()): number {
  return Math.max(0, daysBetween(createdAt, now))
}

/**
 * 多大。`12884901888` → `12.0 GB`。
 *
 * 这里没有句子，只有数字与单位——单位是拉丁字母（`GB`），
 * 与中文之间的空格由调用点的排版负责，不在这个字符串里拼中文。
 *
 * 千进制不是二进制：用户看硬盘容量时脑子里的 GB 是厂商标的那个，
 * 不是 1024³。整数部分到两位就不留小数（`120 GB` 而不是 `120.0 GB`）。
 */
const UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'] as const

export function bytes(value: number): string {
  let n = Math.max(0, value)
  let unit = 0
  while (n >= 1000 && unit < UNITS.length - 1) {
    n /= 1000
    unit += 1
  }
  const digits = unit === 0 || n >= 100 ? 0 : 1
  return `${n.toFixed(digits)} ${UNITS[unit]}`
}

/** 一条指标线现在是多少。最后一个有数的点就是「现在」。 */
export function currentValue(points: { value: number | null }[]): number | null {
  for (let i = points.length - 1; i >= 0; i -= 1) {
    const value = points[i]?.value
    if (value !== null && value !== undefined) return value
  }
  return null
}

/**
 * `7 月 29 日 03:41`。事故那行 meta 的 `{when}`。
 *
 * **月 / 日两个字来自 Intl 的地区数据，不是写在这儿的。**
 * 逐段取出来再用空格拼，为的是 CLAUDE.md §3 那条「数字与中文之间留空格」——
 * `Intl` 直接 format 出来是 `7月29日 03:41`，没有那些空格。
 */
const MONTH_DAY_TIME = new Intl.DateTimeFormat('zh-CN', {
  month: 'long',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

export function monthDayTime(iso: string): string {
  return MONTH_DAY_TIME.formatToParts(new Date(iso))
    .map((part) => part.value)
    .join(' ')
    .replace(/\s+:\s+/, ':')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * 发布页专用：钉死 `Asia/Ho_Chi_Minh`，不跟浏览器时区、也不跟 Lulu/chat 用的
 * `monthDayTime`（未传 timeZone 时在 Node 里落到 UTC）。
 *
 * 拼法与 `monthDayTime` 相同：`formatToParts` 再在数字与中文之间留空格。
 */
const PUBLISH_MONTH_DAY_TIME = new Intl.DateTimeFormat('zh-CN', {
  month: 'long',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'Asia/Ho_Chi_Minh',
})

export function publishMonthDayTime(iso: string): string {
  return PUBLISH_MONTH_DAY_TIME.formatToParts(new Date(iso))
    .map((part) => part.value)
    .join(' ')
    .replace(/\s+:\s+/, ':')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * `4 分钟 12 秒`。事故那行 meta 的 `{duration}`。
 *
 * 单位那两个字同样来自 Intl 的地区数据。
 *
 * **设计稿上写的是「4 分 12 秒」，这里给出的是「4 分钟 12 秒」。**
 * zh-CN 下 `Intl` 最窄的说法就是「分钟」，而把它裁成「分」等于自己造词——
 * 一个靠切字符串得到的中文单位，换个地区数据就悄悄变成别的东西。
 * 要设计稿那个说法的话得有一条模板，已提给流 0（见 m3-需要的契约.md §20）。
 */
function unit(value: number, name: 'day' | 'hour' | 'minute' | 'second'): string {
  return new Intl.NumberFormat('zh-CN', { style: 'unit', unit: name, unitDisplay: 'narrow' })
    .formatToParts(value)
    .map((part) => part.value)
    .join(' ')
}

export function duration(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60

  const parts: string[] = []
  if (h > 0) parts.push(unit(h, 'hour'))
  if (m > 0) parts.push(unit(m, 'minute'))
  // 秒那一段只在「没有更大的单位」或者「它不为 0」时出现：
  // 整 5 分钟说「5 分钟」，不说「5 分钟 0 秒」
  if (s > 0 || parts.length === 0) parts.push(unit(s, 'second'))

  return parts.join(' ')
}

/**
 * 「过去了多久」。`2 天` `3 小时` `20 分钟`。
 *
 * ## 为什么是一段时长，不是一个时刻
 *
 * `host.reach.stale` 的句子是「{since}没看到它了」——**它要的是长度，不是坐标**。
 * 填一个「今天 09:11」进去会读成「今天 09:11没看到它了」，那不是中文。
 *
 * 而 `host.access.stale`（「这是 {checked_at} 存下来的」）要的恰恰相反，
 * 那儿走 `whenChecked`。**两处不通用，因为句子问的不是同一件事。**
 *
 * ## 那两个字仍然来自 Intl，不是我写的
 *
 * 「天」「小时」「分钟」由地区数据给，`formatToParts` 拼回去时留一个空格
 * （CLAUDE.md §3：数字与中文之间留空格）。**源码里一个中文字都没有**——
 * 这不是为了绕过检查，是因为写死的单位换个地区就悄悄变成别的东西。
 */
export function elapsed(iso: string, now: Date = new Date()): string {
  const seconds = Math.max(0, Math.floor((now.getTime() - new Date(iso).getTime()) / 1000))
  if (seconds >= 86400) return unit(Math.floor(seconds / 86400), 'day')
  if (seconds >= 3600) return unit(Math.floor(seconds / 3600), 'hour')
  // **不满一分钟也说「1 分钟」**：这几处的时间粒度是「每 6 小时看一次」，
  // 秒对他没有任何意义，而「0 分钟前」读起来像是坏了
  return unit(Math.max(1, Math.floor(seconds / 60)), 'minute')
}

/**
 * 「多久之前」。`2 天前` `20 分钟前`。
 *
 * 「前」那个字也来自 Intl（`RelativeTimeFormat`），理由同上。
 * 它与 `elapsed` 共用同一套分档，**所以同一个时刻在两句话里说的是同一个长度**——
 * 各写一套的话，「20 分钟前看过它」和「21 分钟没看到它了」会同时出现在一屏上。
 */
export function ago(iso: string, now: Date = new Date()): string {
  const seconds = Math.max(0, Math.floor((now.getTime() - new Date(iso).getTime()) / 1000))
  const [value, name] =
    seconds >= 86400
      ? ([Math.floor(seconds / 86400), 'day'] as const)
      : seconds >= 3600
        ? ([Math.floor(seconds / 3600), 'hour'] as const)
        : ([Math.max(1, Math.floor(seconds / 60)), 'minute'] as const)

  return new Intl.RelativeTimeFormat('zh-CN', { numeric: 'always' })
    .formatToParts(-value, name)
    .map((part) => part.value)
    .join(' ')
    .trim()
}
